// =============================================
// orphanLinkMatcher — fuzzy match sieroty (link do nieistniejącego sluga)
// na realny node z mapy po podobieństwie slug-slug + anchor-title.
// =============================================
//
// Sierota = link wewnętrzny <a href="/X"> gdzie X NIE jest w liveSlugSet
// (brak żywej strony WP) ANI w slugToNode (brak noda w mapie nawet po slugify).
// LLM przekręcił slug (inna forma słowa, doklejony człon, ucięcie). Zwykle
// orphan jest BLISKIEM PUDŁEM realnego artykułu — chcemy zasugerować poprawkę.
//
// Algorytm:
//   score1 = jaccard(slugify(orphanSlug).split("-"), slugify(node.slug).split("-"))
//   score2 = jaccard(normalize(orphanAnchor), normalize(node.title))   * 0.9
//   combined = max(score1, score2)
//   if combined >= minSimilarity AND node.wp_post_url → return match
//   else → null (modal pokaże "brak pewnego dopasowania → strip do <span>")
//
// minSimilarity default 0.75 — uzasadnione:
//   "jaki-model-renault-osobowe-wybrac" vs "jaki-model-renault-osobowy-wybrac"
//     = 4/5 tokens overlap = 0.80 → MATCH ✓
//   "renault-cos-zupelnie-innego" vs "jaki-model-renault"
//     = 1/4 tokens = 0.25 → SKIP ✓
//
// Reuse helpers:
//   - slugify() z @/lib/slugify (ASCII-normalize)
//   - jaccardSimilarity, normalizeForDedup z @/services/contentEngine/textUtils

import { slugify } from "@/lib/slugify";
import {
  jaccardSimilarity,
  normalizeForDedup,
} from "@/services/contentEngine/textUtils";

export interface OrphanCandidate {
  id: string;
  slug: string;
  title: string;
  wp_post_url: string | null;
}

export interface OrphanMatch {
  nodeId: string;
  slug: string;
  wpUrl: string;
  similarity: number;
  reason: string;
}

/**
 * Znajdź najbliższy realny node dla orphan link.
 *
 * @param orphanSlug — decodedSlug ze ścieżki linka (np. "jaki-model-osobowe")
 * @param orphanAnchor — anchor text z <a> (np. "jaki model osobowy")
 * @param candidates — published nody z wp_post_url (filtruje wewnątrz, NIE musi być pre-filtered)
 * @param minSimilarity — próg combined (default 0.75)
 */
export function findOrphanMatch(
  orphanSlug: string,
  orphanAnchor: string,
  candidates: OrphanCandidate[],
  minSimilarity: number = 0.75,
): OrphanMatch | null {
  if (!orphanSlug || candidates.length === 0) return null;

  // Tokeny orphan'a — split po "-", po slugify (uniform ASCII)
  const orphanTokens = tokenizeSlug(orphanSlug);
  if (orphanTokens.size === 0) return null;

  // Tokeny anchor'a (jeśli jest sensowny)
  const orphanAnchorNorm = normalizeForDedup(orphanAnchor || "");

  let best: OrphanMatch | null = null;

  for (const c of candidates) {
    if (!c.wp_post_url) continue; // sierota matcher zwraca tylko żywe targety

    // Score 1: slug-slug. Używamy max(jaccard, containment) z guard'em
    // minSize>=3 — typo "osobowe→osobowy" w 5-tokenowym slugu daje jaccard=0.67
    // ale containment=4/5=0.80 → match. Guard zapobiega false positives gdy
    // bardzo krótki orphan (1-2 tokeny) jest podzbiorem długiego candidate.
    const candidateTokens = tokenizeSlug(c.slug);
    const score1 = scoreSlugSimilarity(orphanTokens, candidateTokens);

    // Score 2: anchor-title (fallback, downweighted 0.9)
    const titleNorm = normalizeForDedup(c.title || "");
    const score2 = orphanAnchorNorm
      ? jaccardSimilarity(orphanAnchorNorm, titleNorm) * 0.9
      : 0;

    const combined = Math.max(score1, score2);

    if (combined >= minSimilarity) {
      if (!best || combined > best.similarity) {
        best = {
          nodeId: c.id,
          slug: c.slug,
          wpUrl: c.wp_post_url,
          similarity: parseFloat(combined.toFixed(2)),
          reason:
            score1 >= score2
              ? `slug tokens jaccard ${combined.toFixed(2)}`
              : `anchor↔title jaccard ${(combined / 0.9).toFixed(2)} (×0.9)`,
        };
      }
    }
  }

  return best;
}

/**
 * Score slug↔slug: max(jaccard, containment) z guard minSize>=3.
 * Pozwala typo (1-token różnica w 5-tokenowym slugu) dawać match,
 * blokując false positives dla bardzo krótkich orphan slugów.
 */
function scoreSlugSimilarity(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  const jacc = jaccardOfSets(a, b);
  const minSize = Math.min(a.size, b.size);
  if (minSize < 3) return jacc; // guard: krótkie sets — tylko jaccard
  // Intersection
  let intersection = 0;
  for (const t of a) if (b.has(t)) intersection++;
  const containment = intersection / minSize;
  return Math.max(jacc, containment);
}

// =============================================
// Helpers (pure)
// =============================================

function tokenizeSlug(slug: string): Set<string> {
  const normalized = slugify(slug || "");
  if (!normalized) return new Set();
  return new Set(normalized.split("-").filter((t) => t.length > 0));
}

function jaccardOfSets(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const t of a) {
    if (b.has(t)) intersection++;
  }
  const union = a.size + b.size - intersection;
  return union > 0 ? intersection / union : 0;
}
